/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import ivimsReducer from '_store/ivim/ivimsReducers';
import { ivimsSagas } from '_store/ivim/sagas';
import { eventChannel } from 'redux-saga';
import { put } from 'redux-saga/effects';
import { call, cancelled, select, take } from 'typed-redux-saga';

import applicationReducer from '../../application/reducer';
import denmsReducer from '../../denm/denmsReducers';
import { denmsSagas } from '../../denm/sagas';
import devicesReducer from '../../devices/devicesReducers';
import spatemsReducer, { spatemsAdapter } from '../../spatem/spatemsReducers';
import camsReducer from '../../vehicles/vehiclesReducers';
import apiReducer from '../reducer';
import store, { RootState } from '_store';
import { DistributionKind } from '_lib/api/types';
import { useAuth } from 'react-oidc-context';

// const roadsUrl = `${process.env.WEB_SOCKET_ENDPOINT}/api/roads/v1alpha1/segments/events`;
// const roadsSegments = ({ emitter, socketRoads, token }) => {
//   socketRoads.onopen = () => {
//     console.log('opening...', token);
//     socketRoads.send(
//       JSON.stringify({
//         event: 'subscribe',
//         data: {
//           topics: ['*.*'],
//           token: token,
//           headers: {
//             traceparent: "00-31d203e42079286316be3c0e7b129e9b-495eb5969729d819-01",
//             tracestate: "sentry=2131343241",
//           }
//         },
//       }),
//     );
//   }
//   socketRoads.onerror = (error) => {
//     console.log('WebSocket error ', error)
//   }
//   socketRoads.onmessage = (e) => {
//     console.log('msg.channel >>>>>>>>>>>>', JSON.parse(e.data));
//   }
// }

const denmsUrl = `${process.env.WEB_SOCKET_ENDPOINT}/api/etsi/v1alpha1/denms/events`;
const ivimsUrl = `${process.env.WEB_SOCKET_ENDPOINT}/api/etsi/v1alpha1/ivims/events`;
const vehiclesUrl = `${process.env.WEB_SOCKET_ENDPOINT}/api/etsi/v1alpha1/cams/events`;
const showErrors = Boolean(process.env.SHOW_WEBSOCKET_ERRORS === 'true');

// Constants for error handling
const UNAUTHORIZED_ERROR = 401;
const CONNECTION_CLOSED_ABNORMALLY = 1006;
const MAX_RECONNECT_ATTEMPTS = 3;

// Helper function to handle authentication errors in WebSocket connections
const handleWebSocketError = (error, wsName) => {
  if (showErrors) {
    console.log(`WebSocket ${wsName} error >>>>>>>>>>>`, error);
  }

  // Check if the error is related to authentication
  if (error && error.code === UNAUTHORIZED_ERROR) {
    console.log(`WebSocket ${wsName} authentication error. Token might be expired.`);
    // Request a new token through the application's authentication flow
    tryToRefreshToken();
    return true;
  }
  return false;
};

// Function to try refreshing the auth token
const tryToRefreshToken = () => {
  try {
    // Get the current user session token
    const currentToken = window.sessionStorage.getItem('token');
    if (!currentToken) {
      console.log('No token available for refresh');
      // Se non c'è token, forza un reload della pagina per il re-login
      window.location.reload();
      return;
    }

    // Verifica se stiamo già gestendo un errore di token scaduto
    const tokenExpiredFlag = window.sessionStorage.getItem('token-refresh-in-progress');
    if (tokenExpiredFlag) {
      console.log('Token refresh già in corso, evito duplicati');
      return;
    }

    // Segna che stiamo gestendo un refresh del token
    window.sessionStorage.setItem('token-refresh-in-progress', 'true');

    // Dispatch action using the api reducer
    store.dispatch(apiReducer.actions.websocketTokenExpired());

    // This will be caught by the application to trigger a token refresh
    console.log('WebSocket requesting token refresh');

    // Rimuovi il flag dopo un timeout
    setTimeout(() => {
      window.sessionStorage.removeItem('token-refresh-in-progress');
    }, 5000);
  } catch (e) {
    console.error('Error trying to refresh token:', e);
    // Rimuovi il flag in caso di errore
    window.sessionStorage.removeItem('token-refresh-in-progress');
  }
};

const denms = ({ emitter, socketDenms, token, createWs }) => {
  let reconnectAttempts = 0;

  // eslint-disable-next-line no-param-reassign
  socketDenms.onopen = () => {
    // Reset reconnect attempts on successful connection
    reconnectAttempts = 0;

    socketDenms.send(
      JSON.stringify({
        event: 'subscribe',
        data: {
          topics: ['*.*'],
          token: token,
        },
      }),
    );
  };

  // eslint-disable-next-line no-param-reassign
  socketDenms.onerror = (error) => {
    // Check if it's an authentication error and handle accordingly
    const isAuthError = handleWebSocketError(error, 'denms');
    if (!isAuthError) {
      emitter(new Error(error));
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketDenms.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);

      // Check for authentication error in the message
      if (data && data.error && data.error.code === UNAUTHORIZED_ERROR) {
        console.log('DENM WebSocket received authentication error in message');
        tryToRefreshToken();
        return;
      }

      // Controlla per errori specifici di refresh token scaduto
      if (
        data &&
        data.error &&
        (data.error === 'invalid_grant' ||
          (data.error_description && data.error_description.includes('Token is not active')))
      ) {
        console.log('DENM WebSocket: refresh token scaduto rilevato');
        // Forza immediatamente un reload invece di tentare il refresh
        window.location.reload();
        return;
      }

      emitter(data);
    } catch (error) {
      console.error('Error parsing DENM WebSocket message:', error);
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketDenms.onclose = (e) => {
    if (e.code === 1005) {
      // eslint-disable-next-line no-console
      console.log('WebSocket: closed');
      // you probably want to end the channel in this case
      // emitter(END);
    } else {
      // eslint-disable-next-line no-console
      console.log(
        'Socket is closed Unexpectedly. Reconnect will be attempted in 1 second.',
        e.reason,
      );

      // Check if it's an authentication error (abnormal closure might indicate auth issues)
      if (e.code === CONNECTION_CLOSED_ABNORMALLY) {
        reconnectAttempts++;
        console.log(
          `DENM WebSocket reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`,
        );

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Max reconnect attempts reached, trying token refresh');
          tryToRefreshToken();
          reconnectAttempts = 0;
          // After requesting token refresh wait longer
          setTimeout(() => createWs(), 3000);
          return;
        }
      }

      setTimeout(() => createWs(), 1000);
    }
  };
};

const ivims = ({ emitter, socketIvims, token, createWs }) => {
  let reconnectAttempts = 0;

  // eslint-disable-next-line no-param-reassign
  socketIvims.onopen = () => {
    // Reset reconnect attempts on successful connection
    reconnectAttempts = 0;

    socketIvims.send(
      JSON.stringify({
        event: 'subscribe',
        data: {
          topics: ['*.*'],
          token: token,
        },
      }),
    );
  };

  // eslint-disable-next-line no-param-reassign
  socketIvims.onerror = (error) => {
    // Check if it's an authentication error and handle accordingly
    const isAuthError = handleWebSocketError(error, 'ivims');
    if (!isAuthError) {
      emitter(new Error(error));
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketIvims.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);

      // Check for authentication error in the message
      if (data && data.error && data.error.code === UNAUTHORIZED_ERROR) {
        console.log('IVIM WebSocket received authentication error in message');
        tryToRefreshToken();
        return;
      }

      // Controlla per errori specifici di refresh token scaduto
      if (
        data &&
        data.error &&
        (data.error === 'invalid_grant' ||
          (data.error_description && data.error_description.includes('Token is not active')))
      ) {
        console.log('IVIM WebSocket: refresh token scaduto rilevato');
        window.location.reload();
        return;
      }

      emitter(data);
    } catch (error) {
      console.error('Error parsing IVIM WebSocket message:', error);
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketIvims.onclose = (e) => {
    if (e.code === 1005) {
      // eslint-disable-next-line no-console
      if (showErrors) {
        console.log('WebSocket: closed');
      }
      // you probably want to end the channel in this case
      // emitter(END);
    } else {
      // eslint-disable-next-line no-console
      console.log(
        'Socket is closed Unexpectedly. Reconnect will be attempted in 1 second.',
        e.reason,
      );

      // Check if it's an authentication error (abnormal closure might indicate auth issues)
      if (e.code === CONNECTION_CLOSED_ABNORMALLY) {
        reconnectAttempts++;
        console.log(
          `IVIM WebSocket reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`,
        );

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Max reconnect attempts reached, trying token refresh');
          tryToRefreshToken();
          reconnectAttempts = 0;
          // After requesting token refresh wait longer
          setTimeout(() => createWs(), 3000);
          return;
        }
      }

      setTimeout(() => createWs(), 1000);
    }
  };
};

const camTimeouts = new Map();

const vehicles = ({ emitter, socketVehicles, token, createWs }) => {
  let reconnectAttempts = 0;

  socketVehicles.onopen = () => {
    // Reset reconnect attempts on successful connection
    reconnectAttempts = 0;

    socketVehicles.send(
      JSON.stringify({
        event: 'subscribe',
        data: {
          topics: ['*.*'],
          token: token,
        },
      }),
    );
  };

  socketVehicles.onerror = (error) => {
    // Check if it's an authentication error and handle accordingly
    const isAuthError = handleWebSocketError(error, 'vehicles');
    if (!isAuthError && showErrors) {
      console.log('WebSocket error >>>>>>>>>>> ', error);
      emitter(new Error(error));
    }
  };

  socketVehicles.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);

      // Check for authentication error in the message
      if (data && data.error && data.error.code === UNAUTHORIZED_ERROR) {
        console.log('Vehicles WebSocket received authentication error in message');
        tryToRefreshToken();
        return;
      }

      // Controlla per errori specifici di refresh token scaduto
      if (
        data &&
        data.error &&
        (data.error === 'invalid_grant' ||
          (data.error_description && data.error_description.includes('Token is not active')))
      ) {
        console.log('Vehicles WebSocket: refresh token scaduto rilevato');
        window.location.reload();
        return;
      }

      emitter(data);
    } catch (error) {
      console.error('Error parsing Vehicles WebSocket message:', error);
    }
  };

  socketVehicles.onclose = (e) => {
    if (e.code === 1005) {
      console.log('WebSocket: closed');
      // you probably want to end the channel in this case
      // emitter(END);
    } else {
      console.log(
        'Socket is closed Unexpectedly. Reconnect will be attempted in 1 second.',
        e.reason,
      );

      // Check if it's an authentication error (abnormal closure might indicate auth issues)
      if (e.code === CONNECTION_CLOSED_ABNORMALLY) {
        reconnectAttempts++;
        console.log(
          `Vehicles WebSocket reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`,
        );

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Max reconnect attempts reached, trying token refresh');
          tryToRefreshToken();
          reconnectAttempts = 0;
          // After requesting token refresh wait longer
          setTimeout(() => createWs(), 3000);
          return;
        }
      }

      setTimeout(() => createWs(), 1000);
    }
    camTimeouts.forEach(clearTimeout);
  };
  return () => {
    socketVehicles.close();
  };
};

function* handleCamUpdated(cam) {
  const { stationId } = cam;

  if (camTimeouts.has(stationId)) {
    clearTimeout(camTimeouts.get(stationId));
  }

  const timeout = setTimeout(() => {
    store.dispatch(camsReducer.actions.camRemove(stationId));
    camTimeouts.delete(stationId);
  }, 30000);

  camTimeouts.set(stationId, timeout);
}

const devicesUrl = `${process.env.WEB_SOCKET_ENDPOINT}/api/devices/v1alpha1/rsus/events`;
const spatemsUrl = `${process.env.WEB_SOCKET_ENDPOINT}/api/etsi/v1alpha1/spatems/events`;

const devices = ({ emitter, socketDevices, token, createWs }) => {
  let reconnectAttempts = 0;

  // eslint-disable-next-line no-param-reassign
  socketDevices.onopen = () => {
    // Reset reconnect attempts on successful connection
    reconnectAttempts = 0;

    socketDevices.send(
      JSON.stringify({
        event: 'subscribe',
        data: {
          topics: ['**'],
          token: token,
        },
      }),
    );
  };

  // eslint-disable-next-line no-param-reassign
  socketDevices.onerror = (error) => {
    // Check if it's an authentication error and handle accordingly
    const isAuthError = handleWebSocketError(error, 'devices');
    if (!isAuthError && showErrors) {
      console.log('WebSocket devices error >>>>>>>>>>> ', error);
      emitter(new Error(error));
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketDevices.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);

      // Check for authentication error in the message
      if (data && data.error && data.error.code === UNAUTHORIZED_ERROR) {
        console.log('Devices WebSocket received authentication error in message');
        tryToRefreshToken();
        return;
      }

      // Controlla per errori specifici di refresh token scaduto
      if (
        data &&
        data.error &&
        (data.error === 'invalid_grant' ||
          (data.error_description && data.error_description.includes('Token is not active')))
      ) {
        console.log('Devices WebSocket: refresh token scaduto rilevato');
        window.location.reload();
        return;
      }

      emitter(data);
    } catch (error) {
      console.error('Error parsing Devices WebSocket message:', error);
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketDevices.onclose = (e) => {
    if (e.code === 1005) {
      // eslint-disable-next-line no-console
      console.log('WebSocket: closed');
      // you probably want to end the channel in this case
      // emitter(END);
    } else {
      // eslint-disable-next-line no-console
      console.log(
        'Socket is closed Unexpectedly. Reconnect will be attempted in 1 second.',
        e.reason,
      );

      // Check if it's an authentication error (abnormal closure might indicate auth issues)
      if (e.code === CONNECTION_CLOSED_ABNORMALLY) {
        reconnectAttempts++;
        console.log(
          `Devices WebSocket reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`,
        );

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Max reconnect attempts reached, trying token refresh');
          tryToRefreshToken();
          reconnectAttempts = 0;
          // After requesting token refresh wait longer
          setTimeout(() => createWs(), 3000);
          return;
        }
      }

      setTimeout(() => createWs(), 1000);
    }
  };
};

const { sessionStorage } = window;
let socketDenms;
let socketIvims;
let socketDevices;
let socketSpatems;
let socketVehicles;

function initDenmsWebsocket(token: string) {
  return eventChannel((emitter) => {
    function createWs() {
      socketDenms = new WebSocket(denmsUrl);
      denms({ emitter, socketDenms, token, createWs });
    }
    createWs();

    return () => {
      // eslint-disable-next-line no-console
      console.log('Socket off');
      socketDenms.send(
        JSON.stringify({
          event: 'unsubscribe',
          data: {
            id: sessionStorage.getItem('denmSubscriptionID'),
          },
        }),
      );
      sessionStorage.setItem('denmSubscriptionID', '');
    };
  });
}

function initIvimsWebsocket(token: string) {
  return eventChannel((emitter) => {
    function createWs() {
      socketIvims = new WebSocket(ivimsUrl);
      ivims({ emitter, socketIvims, token, createWs });
    }
    createWs();

    return () => {
      // eslint-disable-next-line no-console
      console.log('Socket off');
      socketIvims.send(
        JSON.stringify({
          event: 'unsubscribe',
          data: {
            id: sessionStorage.getItem('ivimSubscriptionID'),
          },
        }),
      );
      sessionStorage.setItem('ivimSubscriptionID', '');
    };
  });
}

function initDevicesWebsocket(token: string) {
  return eventChannel((emitter) => {
    function createWs() {
      socketDevices = new WebSocket(devicesUrl);
      devices({ emitter, socketDevices, token, createWs });
    }
    createWs();

    return () => {
      // eslint-disable-next-line no-console
      console.log('Socket off');
      socketDevices.send(
        JSON.stringify({
          event: 'unsubscribe',
          data: {
            id: sessionStorage.getItem('devicesSubscriptionID'),
          },
        }),
      );
      sessionStorage.setItem('devicesSubscriptionID', '');
    };
  });
}

const spatems = ({ emitter, socketSpatems, token, createWs }) => {
  let reconnectAttempts = 0;

  // eslint-disable-next-line no-param-reassign
  socketSpatems.onopen = () => {
    // Reset reconnect attempts on successful connection
    reconnectAttempts = 0;

    socketSpatems.send(
      JSON.stringify({
        event: 'subscribe',
        data: {
          topics: ['**'],
          token: token,
        },
      }),
    );
  };

  // eslint-disable-next-line no-param-reassign
  socketSpatems.onerror = (error) => {
    // Check if it's an authentication error and handle accordingly
    const isAuthError = handleWebSocketError(error, 'spatems');
    if (!isAuthError && showErrors) {
      console.log('WebSocket spatems error >>>>>>>>>>> ', error);
      emitter(new Error(error));
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketSpatems.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);

      // Check for authentication error in the message
      if (data && data.error && data.error.code === UNAUTHORIZED_ERROR) {
        console.log('Spatems WebSocket received authentication error in message');
        tryToRefreshToken();
        return;
      }

      // Controlla per errori specifici di refresh token scaduto
      if (
        data &&
        data.error &&
        (data.error === 'invalid_grant' ||
          (data.error_description && data.error_description.includes('Token is not active')))
      ) {
        console.log('Spatems WebSocket: refresh token scaduto rilevato');
        window.location.reload();
        return;
      }

      emitter(data);
    } catch (error) {
      console.error('Error parsing Spatems WebSocket message:', error);
    }
  };

  // eslint-disable-next-line no-param-reassign
  socketSpatems.onclose = (e) => {
    if (e.code === 1005) {
      // eslint-disable-next-line no-console
      console.log('WebSocket: closed');
      // you probably want to end the channel in this case
      // emitter(END);
    } else {
      // eslint-disable-next-line no-console
      console.log(
        'Socket is closed Unexpectedly. Reconnect will be attempted in 1 second.',
        e.reason,
      );

      // Check if it's an authentication error (abnormal closure might indicate auth issues)
      if (e.code === CONNECTION_CLOSED_ABNORMALLY) {
        reconnectAttempts++;
        console.log(
          `Spatems WebSocket reconnect attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`,
        );

        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.log('Max reconnect attempts reached, trying token refresh');
          tryToRefreshToken();
          reconnectAttempts = 0;
          // After requesting token refresh wait longer
          setTimeout(() => createWs(), 3000);
          return;
        }
      }

      setTimeout(() => createWs(), 1000);
    }
  };
};

function initSpatemsWebsocket(token: string) {
  return eventChannel((emitter) => {
    function createWs() {
      socketSpatems = new WebSocket(spatemsUrl);
      spatems({ emitter, socketSpatems, token, createWs });
    }
    createWs();

    return () => {
      // eslint-disable-next-line no-console
      console.log('Socket off');
      socketSpatems.send(
        JSON.stringify({
          event: 'unsubscribe',
          data: {
            id: sessionStorage.getItem('spatemsSubscriptionID'),
          },
        }),
      );
      sessionStorage.setItem('spatemsSubscriptionID', '');
    };
  });
}

function initVehiclesWebsocket(token: string) {
  return eventChannel((emitter) => {
    function createWs() {
      socketVehicles = new WebSocket(vehiclesUrl);
      vehicles({ emitter, socketVehicles, token, createWs });
    }
    createWs();

    return () => {
      // eslint-disable-next-line no-console
      console.log('Socket off');
      socketVehicles.send(
        JSON.stringify({
          event: 'unsubscribe',
          data: {
            id: sessionStorage.getItem('vehiclesSubscriptionID'),
          },
        }),
      );
      sessionStorage.setItem('vehiclesSubscriptionID', '');
    };
  });
}

export function* pollTaskWatcherDENM(token: string) {
  // eslint-disable-next-line no-console
  console.log('pollTaskWatcher DENM START >>>>>>>>>>>>');
  const denmChannel = yield call(initDenmsWebsocket, token);
  while (true) {
    const denmAction = yield take(denmChannel);
    console.log('denmevets', denmAction);
    switch (denmAction.event) {
      case 'subscription.created':
        // eslint-disable-next-line no-console
        console.log('denm subscription.created >>>>>>> ');
        sessionStorage.setItem('denmSubscriptionID', denmAction.data.id);
        break;
      case 'subscription.error':
        // eslint-disable-next-line no-console
        if (showErrors) {
          console.log('Websocket DENM fails >>>>>>> ');
          yield put(applicationReducer.actions.toastAdded({ id: 'Websocket DENM fails' }));
        }
        break;
      case 'denm.created':
        // eslint-disable-next-line no-console
        console.log('denm.created >>>>>>> ', denmAction);
        yield put(denmsReducer.actions.denmsAddOne(denmAction.data.denm));
        yield put(denmsSagas.actions.statusTimer(denmAction.data.denm));
        break;
      case 'denm.updated':
        // eslint-disable-next-line no-console
        console.log('denm.updated >>>>>>> ', denmAction);
        yield put(denmsReducer.actions.denmUpsertOne(denmAction.data.denm));
        break;
      case 'denm.deleted':
        // eslint-disable-next-line no-console
        console.log('denm.deleted >>>>>>> ', denmAction);
        yield put(denmsReducer.actions.denmRemove(denmAction.data.denm.id));
        break;
      default:
        if (showErrors) {
          // eslint-disable-next-line no-console
          console.log('Websocket DENM fails >>>>>>> ', denmAction.event, denmAction);
          yield put(applicationReducer.actions.toastAdded({ id: 'Websocket DENM fails' }));
        }
        break;
    }
  }
}

export function* pollTaskWatcherIVIM(token: string) {
  // eslint-disable-next-line no-console
  console.log('pollTaskWatcher IVIM START >>>>>>>>>>>>');
  const ivimChannel = yield call(initIvimsWebsocket, token);
  while (true) {
    const ivimAction = yield take(ivimChannel);
    console.log('ivim events', ivimAction, ivimAction.event);
    switch (ivimAction.event) {
      case 'subscription.created':
        // eslint-disable-next-line no-console
        console.log('ivim subscription.created >>>>>>> ');
        sessionStorage.setItem('ivimSubscriptionID', ivimAction.data.id);
        break;
      case 'subscription.error':
        // eslint-disable-next-line no-console
        if (showErrors) {
          console.log('Websocket IVIM fails >>>>>>> ');
          yield put(applicationReducer.actions.toastAdded({ id: 'Websocket IVIM fails' }));
        }
        break;
      case 'ivim.created':
        // eslint-disable-next-line no-console
        console.log('ivim.created >>>>>>> ', ivimAction);
        yield put(ivimsReducer.actions.ivimsAddOne(ivimAction.data.ivim));
        yield put(ivimsSagas.actions.statusTimer(ivimAction.data.ivim));
        break;
      case 'ivim.deleted':
        // eslint-disable-next-line no-console
        console.log('ivim.deleted >>>>>>> ', ivimAction);
        yield put(ivimsReducer.actions.ivimRemove(ivimAction.data.ivim.id));
        break;
      default:
        if (showErrors) {
          // eslint-disable-next-line no-console
          console.log('Websocket IVIM fails >>>>>>> ', ivimAction.event, ivimAction);
          yield put(applicationReducer.actions.toastAdded({ id: 'Websocket IVIM fails' }));
        }
        break;
    }
  }
}

export function* pollTaskWatcherRSU(token: string) {
  // eslint-disable-next-line no-console
  console.log('pollTaskWatcher RSU START >>>>>>>>>>>>');
  const devicesChannel = yield call(initDevicesWebsocket, token);
  while (true) {
    const devicesAction = yield take(devicesChannel);
    switch (devicesAction.event) {
      case 'subscription.created':
        // eslint-disable-next-line no-console
        console.log('rsu subscription.created >>>>>>> ');
        sessionStorage.setItem('devicesSubscriptionID', devicesAction.data.id);
        break;
      case 'subscription.error':
        // eslint-disable-next-line no-console
        if (showErrors) {
          console.log('Websocket RSU fails error >>>>>>> ', devicesAction.event);
          // yield put(applicationReducer.actions.toastAdded({ id: 'Websocket RSU fails' }));
        }
        break;
      case 'rsu.created':
        // eslint-disable-next-line no-console
        console.log('rsu.created >>>>>>> ', devicesAction);
        yield put(devicesReducer.actions.devicesAddOne(devicesAction.data.rsu));
        break;
      case 'rsu.updated':
        // eslint-disable-next-line no-console
        console.log('rsu.updated >>>>>>> ', devicesAction);
        yield put(devicesReducer.actions.devicesUpsertOne(devicesAction.data.rsu));
        break;
      case 'rsu.deleted':
        // eslint-disable-next-line no-console
        console.log('rsu.deleted >>>>>>> ', devicesAction);
        yield put(devicesReducer.actions.devicesRemove(devicesAction.data.rsu.id));
        break;
      case 'rsu.diagnostic.online':
      case 'rsu.diagnostic.degraded':
        // eslint-disable-next-line no-console
        // console.log('rsu.diagnostic >>>>>', devicesAction);
        yield put(apiReducer.actions.upsertDiagnostic(devicesAction));
        break;
      case 'rsu.diagnostic.offline':
        yield put(apiReducer.actions.deleteDiagnostic(devicesAction));
        break;
      default:
        if (showErrors) {
          // eslint-disable-next-line no-console
          console.log('Websocket RSU fails >>>>>>> ', devicesAction.event, devicesAction);
          // yield put(applicationReducer.actions.toastAdded({ id: 'Websocket RSU fails' }));
        }
        break;
    }
  }
}

export function* pollTaskWatcherSPATEM(token: string) {
  // eslint-disable-next-line no-console
  console.log('pollTaskWatcher SPATEM START >>>>>>>>>>>>');
  const spatemsChannel = yield call(initSpatemsWebsocket, token);
  while (true) {
    const spatemsAction = yield take(spatemsChannel);
    switch (spatemsAction.event) {
      case 'subscription.created':
        // eslint-disable-next-line no-console
        console.log('spatem subscription.created >>>>>>> ');
        sessionStorage.setItem('spatemsSubscriptionID', spatemsAction.data.id);
        break;
      case 'subscription.error':
        // eslint-disable-next-line no-console
        if (showErrors) {
          console.log('Websocket SPATEM fails error >>>>>>> ', spatemsAction.event);
          // yield put(applicationReducer.actions.toastAdded({ id: 'Websocket SPATEM fails' }));
        }
        break;
      case 'spatem.upsert': {
        // Transform the incoming SPATEM data to match expected format
        const { spatem: spatemData } = spatemsAction.data;
        if (spatemData && spatemData.id) {
          // Get current state to find existing SPATEM by identifier
          const state: RootState = yield select();
          const currentSpatems = spatemsAdapter.getSelectors().selectAll(state.spatems);

          // Find existing SPATEM by matching identifier with incoming spatem.id
          const existingSpatem = currentSpatems.find((s) => s.identifier === spatemData.id);

          if (existingSpatem) {
            // Convert microdegrees to degrees (divide by 1e7)
            const lat = spatemData.coords?.lat ? spatemData.coords.lat / 1e7 : 0;
            const lng = spatemData.coords?.long ? spatemData.coords.long / 1e7 : 0;
            
            // Build the payload object from the incoming spatem data
            const updatedPayload = {
              id: spatemData.id,
              coords: {
                lat: spatemData.coords?.lat || existingSpatem.payload.coords.lat,
                long: spatemData.coords?.long || existingSpatem.payload.coords.long,
              },
              region: spatemData.region ?? existingSpatem.payload.region,
              timestamp: spatemData.timestamp || existingSpatem.payload.timestamp,
              intersection_id: spatemData.intersection_id ?? existingSpatem.payload.intersection_id,
              mapem_timestamp: spatemData.mapem_timestamp || existingSpatem.payload.mapem_timestamp,
              status: spatemData.status ?? existingSpatem.payload.status,
              moy: spatemData.moy ?? existingSpatem.payload.moy,
            };
            
            // Update the payload and related fields of the existing SPATEM
            yield put(
              spatemsReducer.actions.spatemsUpdate({
                id: existingSpatem.id,
                changes: {
                  payload: updatedPayload,
                  updated_at: new Date().toISOString(),
                  location: {
                    type: 'Point' as const,
                    coordinates: [lng, lat],
                  },
                },
              }),
            );
          } else {
            // If not found, create a new SPATEM (fallback behavior)
            const lat = spatemData.coords?.lat ? spatemData.coords.lat / 1e7 : 0;
            const lng = spatemData.coords?.long ? spatemData.coords.long / 1e7 : 0;
            
            // Build the payload object from the incoming spatem data
            const payload = {
              id: spatemData.id,
              coords: {
                lat: spatemData.coords?.lat || 0,
                long: spatemData.coords?.long || 0,
              },
              region: spatemData.region || 0,
              timestamp: spatemData.timestamp || new Date().toISOString(),
              intersection_id: spatemData.intersection_id || 0,
              mapem_timestamp: spatemData.mapem_timestamp || new Date().toISOString(),
              status: spatemData.status,
              moy: spatemData.moy,
            };
            
            const transformedSpatem = {
              id: `${spatemData.id}-${Date.now()}`, // Generate unique ID
              identifier: spatemData.id,
              payload,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              location: {
                type: 'Point' as const,
                coordinates: [lng, lat] as [number, number],
              },
              name: spatemData.id,
              alive: true,
              // Add default distribution for compatibility with CardDetails component
              distribution: {
                kind: DistributionKind.UDP as DistributionKind.UDP,
                host: '',
                port: 0,
              },
            };
            yield put(spatemsReducer.actions.spatemsUpsertOne(transformedSpatem));
          }
        }
        break;
      }
      default:
        if (showErrors) {
          // eslint-disable-next-line no-console
          console.log('Websocket SPATEM fails >>>>>>> ', spatemsAction.event, spatemsAction);
          // yield put(applicationReducer.actions.toastAdded({ id: 'Websocket SPATEM fails' }));
        }
        break;
    }
  }
}

export function* pollTaskWatcherCAM(token: string) {
  // eslint-disable-next-line no-console
  console.log('pollTaskWatcher CAM START >>>>>>>>>>>>');
  const camChannel = yield call(initVehiclesWebsocket, token);
  try {
    while (true) {
      const camAction = yield take(camChannel);
      // console.log('cam events', camAction, camAction.event);
      switch (camAction.event) {
        case 'subscription.created':
          console.log('cam subscription.created >>>>>>> ');
          sessionStorage.setItem('camSubscriptionID', camAction.data.id);
          break;
        case 'subscription.error':
          if (showErrors) {
            console.log('Websocket CAM fails >>>>>>> ');
            yield put(applicationReducer.actions.toastAdded({ id: 'Websocket CAM fails' }));
          }
          break;
        case 'cam.created':
          //console.log('cam.created >>>>>>> ', camAction);
          yield put(camsReducer.actions.camsAddOne(camAction.data.cam));
          break;
        case 'cam.updated':
          //console.log('cam.updated >>>>>>> ', camAction);
          if (camAction.data.cam.pathHistory?.coordinates.length > 1) {
            yield call(handleCamUpdated, camAction.data.cam);
          }
          yield put(camsReducer.actions.camUpsertOne(camAction.data.cam));
          break;
        case 'cam.deleted':
          //console.log('cam.deleted >>>>>>> ', camAction);
          yield put(camsReducer.actions.camRemove(camAction.data.cam.stationId));
          break;
        default:
          if (showErrors) {
            console.log('Websocket CAM fails >>>>>>> ', camAction.event, camAction);
            yield put(applicationReducer.actions.toastAdded({ id: 'Websocket CAM fails' }));
          }
          break;
      }
    }
  } finally {
    if (yield cancelled()) {
      camTimeouts.forEach(clearTimeout);
      camTimeouts.clear();
    }
  }
}
